<template>
  <div>
    <!-- Loader -->
    <div v-if="showLoader || checkingBalance" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <div v-else class="w-full mx-auto px-6 py-2 bg-white shadow-md rounded-lg">

      <!-- Outstanding Balance Warning -->
      <div
        v-if="showBalanceWarning"
        class="mb-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded flex items-center"
      >
        <svg
          class="h-6 w-6 text-custom-yellow mr-2"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01M12 4a8 8 0 100 16 8 8 0 000-16z"
          />
        </svg>
        <p>
          Outstanding laboratory balance is
          <span class="font-semibold">KES {{ formatPrice(outstandingBalance) }}</span>
        </p>
      </div>

      <!-- Insurance Coverage Notice -->
      <div
        v-if="showInsuranceNotice"
        class="mb-6 p-4 bg-green-100 border-l-4 border-green-500 text-green-700 rounded flex items-center"
      >
        <svg
          class="h-6 w-6 text-green-500 mr-2"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p>
          The patient is covered by insurance. You can proceed with the lab test.
        </p>
      </div>
      <h2 class="text-2xl font-bold mb-6">Lab Request Details</h2>

      <div class="grid grid-cols-2 gap-6">
        <div>
          <div class="mb-2"><h3 class="font-bold">Visit Code:</h3><p>{{ visit?.VisitCode }}</p></div>
          <div class="mb-2"><h3 class="font-bold">Patient Name:</h3><p>{{ visit.patient.FullName }}</p></div>

          <div class="mb-2"><h3 class="font-bold">Age:</h3><p>{{ visit.patient.Age }}</p></div>
          <div class="mb-2"><h3 class="font-bold">Gender:</h3><p>{{ visit.patient.Gender }}</p></div>
        </div>
        <div>
          <div class="mb-2"><h3 class="font-bold">Patient Unique Code:</h3><p>{{ visit.patient.unique_code }}</p></div>
          <div class="mb-2"><h3 class="font-bold">Requested Date/Time:</h3><p>{{ visit?.lab_tests?.[0]?.created_at || "-" }}</p></div>
          <div class="mb-2"><h3 class="font-bold">Requesting Doctor:</h3><p>{{ visit.lab_tests?.[0]?.requested_by?.Username }}</p></div>
          <div class="mb-2"><h3 class="font-bold">Priority:</h3>
            <p :class="{ 'text-red-600': priority === 'Urgent' || priority === 'STAT' }">{{ priority }}</p>
          </div>
        </div>
      </div>

      <div class="mt-6">
        <h2 class="text-xl font-bold mb-6 text-gray-700">Laboratory Request</h2>
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-100">
            <tr>
              <th class="px-6 py-2 text-left">Visit ID</th>
              <th class="px-6 py-2 text-left">Test Requested</th>
              <th class="px-6 py-2 text-left">Clinical Notes</th>
              <th class="px-6 py-2 text-left">Test Status</th>
              <th class="px-6 py-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(request, index) in visit.lab_tests" :key="request.id">
              <td class="px-6 py-1">{{ index + 1 }}</td>
              <td class="px-6 py-1">{{ request.test_type.name }}</td>
              <td class="px-6 py-1">{{ request.notes }}</td>
              <td class="px-6 py-1">
                <span v-if="request.status.code === 'ST-003'" class="px-2 py-1 text-xs font-semibold bg-blue-500 text-white rounded">Completed</span>
                <span v-else-if="request.status.code === 'ST-006'" class="px-2 py-1 text-xs font-semibold bg-yellow-400 text-white rounded">Pending</span>
                <span v-else-if="request.status.code === 'ST-011'" class="px-2 py-1 text-xs font-semibold bg-yellow-700 text-white rounded">Collected</span>
                <span v-else-if="request.status.code === 'ST-012'" class="px-2 py-1 text-xs font-semibold bg-orange-500 text-white rounded">Recollect</span>
                <span v-else-if="request.status.code === 'ST-013'" class="px-2 py-1 text-xs font-semibold bg-green-500 text-white rounded">Published</span>
                <span v-else-if="request.status.code === 'ST-009'" class="px-2 py-1 text-xs font-semibold bg-red-500 text-white rounded">Rejected</span>
              </td>
              <td class="px-6 py-1">
                <button v-if="request.status.code === 'ST-006'" @click="handleRejectionModal(request)" class="px-3 py-1 text-sm bg-red-500 text-white rounded">Reject</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-8 flex justify-end mb-4">
        <button @click="acceptRequest" :disabled="checkingBalance" class="px-6 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600 font-medium">
          Proceed
        </button>
      </div>

      <!-- Laboratory Modal -->
      <LaboratoryQueueModal
        :show="showLaboratoryModal"
        :patientId="selectedPatientId"
        :patient="visit.patient"
        :visitDetails="visitDetailsOnly"
        :labTest="rejectingLabTest"
        :labTests="visit.lab_tests"
        :isRejection="isRejection"
        @close="closeLaboratoryQueueModal"
        @submit="handleAppointmentSubmit"
        @refetch-requests="getPatientVisitLabRequests(false)"
      />

      <!-- Balance Warning Popup -->
      <div
        v-if="showBalanceWarningModal"
        class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        role="dialog"
        aria-labelledby="balance-warning-title"
        aria-modal="true"
      >
        <div class="bg-white rounded-lg shadow-md p-6 w-full max-w-md mx-4">
          <div class="flex items-center mb-4">
            <svg class="h-6 w-6 text-custom-yellow mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M12 4a8 8 0 100 16 8 8 0 000-16z" />
            </svg>
            <h2 id="balance-warning-title" class="text-xl font-bold text-gray-700">
              Outstanding Balance
            </h2>
          </div>
          <p class="text-gray-600 mb-6">
            The patient has an outstanding balance of
            <span class="font-semibold">KES {{ outstandingBalance }}</span>
            that must be paid before proceeding with the lab request.
          </p>
          <div class="flex justify-end">
            <button
              @click="showBalanceWarningModal = false"
              class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
            >
              Close
            </button>
            <!-- <button
              @click="redirectToPayment"
              class="bg-custom-yellow text-white px-4 py-2 rounded hover:bg-yellow-600 transition-colors"
            >
              Proceed to Payment
            </button> -->
          </div>
        </div>
      </div>

      <!-- Cash Payment Warning Popup -->
      <div
        v-if="showCashPaymentWarning"
        class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        role="dialog"
        aria-labelledby="cash-payment-warning-title"
        aria-modal="true"
      >
        <div class="bg-white rounded-lg shadow-md p-6 w-full max-w-md mx-4">
          <div class="flex items-center mb-4">
            <svg
              class="h-6 w-6 text-custom-yellow mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01M12 4a8 8 0 100 16 8 8 0 000-16z"
              />
            </svg>
            <h2 id="cash-payment-warning-title" class="text-xl font-bold text-gray-700">
              Cash Payment Required
            </h2>
          </div>
          <p class="text-gray-600 mb-6">
            A cash payment of
            <span class="font-semibold">KES {{ formatPrice(outstandingBalance) }}</span>
            is required to proceed with the lab request.
          </p>
          <div class="flex justify-end gap-4">
            <button @click="showCashPaymentWarning = false" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LaboratoryQueueModal from "@/views/modals/LaboratoryQueueModal.vue";
import apiClient from "@/services/api";

export default {
  name: "LabRequestDetails",
  components: { LaboratoryQueueModal },
  data() {
    return {
      showLaboratoryModal: false,
      isRejection: false,
      visitId: "",
      showLoader: false,
      showBalanceWarning: false,
      showBalanceWarningModal: false,
      showCashPaymentWarning: false,
      showInsuranceNotice: false,
      checkingBalance: false,
      rejectingLabTest: "",
      outstandingBalance: 0,
      selectedPatientId: "",
      visit: { patient: {}, lab_tests: [] },
      visitDetailsOnly: {},
    };
  },
  mounted() {
    this.visitId = this.$route.params.lab_request_id;
    this.getPatientVisitLabRequests(true);
  },
  computed: {
    priority() {
      const tests = this.visit.lab_tests;
      if (tests.some(t => t.queue_priority === "STAT")) return "STAT";
      if (tests.some(t => t.queue_priority === "Urgent")) return "Urgent";
      return "Normal";
    },
    formatPrice() {
      return (val) => parseFloat(val || 0).toLocaleString("en-KE", { minimumFractionDigits: 2 });
    },
  },
  methods: {
    async getPatientVisitLabRequests(displayLoader = true) {
      try {
        if (displayLoader) this.showLoader = true;

        const labRes = await apiClient.getPatientVisitLabTests(this.visitId);
        if (labRes.success) {
          this.visit = labRes.data;
          const { VisitCode, VisitEndTime, VisitStartTime, VisitId, VisitType } = labRes.data;
          this.visitDetailsOnly = { VisitCode, VisitEndTime, VisitStartTime, VisitId, VisitType };
        }

        // Check if patient has insurance - show insurance notice if covered
        await this.checkInsuranceCoverage();

        // Load lab-only outstanding balance via coverage endpoint
        await this.loadLabOutstandingBalance();

      } catch (err) {
        this.$toast.error("Failed to load lab request");
      } finally {
        if (displayLoader) this.showLoader = false;
      }
    },
    // Check if patient has insurance and show appropriate notice
    async checkInsuranceCoverage() {
      try {
        const billRes = await apiClient.getBillByVisit(this.visitId);
        if (!billRes.success || !billRes.data) {
          this.showInsuranceNotice = false;
          return;
        }

        const bill = billRes.data;
        const hasInsurance = bill.bill_payment_methods && 
          bill.bill_payment_methods.some(method => method.payment_method_type === 'insurance');

        if (hasInsurance) {
          const coverageRes = await apiClient.verifyLaboratoryCoverage({
            patient_id: this.visit.patient?.PatientId,
            visit_id: this.visitId,
            bill_id: bill.id,
          });

          if (coverageRes.success && coverageRes.data?.is_covered) {
            this.showInsuranceNotice = true;
          } else {
            this.showInsuranceNotice = false;
          }
        } else {
          this.showInsuranceNotice = false;
        }
      } catch {
        this.showInsuranceNotice = false;
      }
    },
    async acceptRequest() {
      this.checkingBalance = true;
      this.showCashPaymentWarning = false;

      try {
        const billRes = await apiClient.getBillByVisit(this.visitId);
        if (!billRes.success || !billRes.data) {
          this.$toast.error("Bill not found");
          return;
        }

        const bill = billRes.data;

        const coverageRes = await apiClient.verifyLaboratoryCoverage({
          patient_id: this.visit.patient.PatientId,
          visit_id: this.visitId,
          bill_id: bill.id,
        });

        if (!coverageRes.success) {
          this.$toast.error("Failed to verify coverage");
          return;
        }

        const coverage = coverageRes.data;

        // Always update the banner with the latest lab-only balance
        this.outstandingBalance = parseFloat(coverage.remaining_balance || 0);

        if (coverage.is_covered || coverage.settle_at_end_allowed) {
          this.showLaboratoryModal = true;
          this.isRejection = false;
        } else {
          this.showCashPaymentWarning = true;
        }

      } catch (error) {
        console.error("Error:", error);
        this.$toast.error("Error checking coverage");
      } finally {
        this.checkingBalance = false;
      }
    },

    handleRejectionModal(labTest) {
      this.rejectingLabTest = labTest;
      this.showLaboratoryModal = true;
      this.isRejection = true;
    },

    closeLaboratoryQueueModal() {
      this.showLaboratoryModal = false;
      this.showBalanceWarning = false;
      this.showBalanceWarningModal = false;
      this.showCashPaymentWarning = false;
      this.showInsuranceNotice = false;
    },

    handleAppointmentSubmit() {
      this.closeLaboratoryQueueModal();
      this.getPatientVisitLabRequests(false);
    },
    // Fetches only the laboratory portion of outstanding balance for the banner.
    // Uses the same coverage endpoint so the balance is scoped to billing_category: 2.
    async loadLabOutstandingBalance() {
      try {
        const billRes = await apiClient.getBillByVisit(this.visitId);
        if (!billRes.success || !billRes.data) return;

        const coverageRes = await apiClient.verifyLaboratoryCoverage({
          patient_id: this.visit.patient?.PatientId,
          visit_id: this.visitId,
          bill_id: billRes.data.id,
        });

        if (coverageRes.success && coverageRes.data) {
          this.outstandingBalance = parseFloat(coverageRes.data.remaining_balance || 0);
        }
      } catch {
        // Silently fail
      }
    },
    redirectToPayment() {
      if (this.visitId) {
        this.$router.push(`/payment/${this.visitId}`);
        this.showCashPaymentWarning = false;
        this.showBalanceWarning = false;
        this.showBalanceWarningModal = false;
      }
    },
  },
};
</script>